import { toast } from '@heroui/react';
import type { i18n as I18n } from 'i18next';
import { getI18n } from 'react-i18next';
import type {
    LocalizedToastOptions,
    LocalizedToastVariant,
    ToastCopy,
} from './localizedToastTypes';

/**
 * A copy line in the language on screen. It reads the instance
 * `initReactI18next` registered, as `useTranslation` does in this app (it
 * has no `I18nextProvider`); with none registered (a test that loads no
 * copy) it gives the key back, as `useTranslation`'s fallback does.
 */
const translate = ({ key, params }: ToastCopy): string => {
    const i18n = getI18n() as I18n | undefined;
    return i18n ? i18n.t(key, { ns: 'pages', ...params }) : key;
};

/**
 * Shows a toast whose copy a hook gave as keys (rulebook §7: hooks return
 * keys, `t()` runs in a `.tsx`), and returns its id.
 *
 * - With no `options` the toast is the title alone; with them, only the
 *   fields given are passed on.
 * - **The action** is a small secondary button. Pressing it closes the
 *   toast, then runs `onPress`.
 */
export const showLocalizedToast = (
    variant: LocalizedToastVariant,
    title: ToastCopy,
    options?: LocalizedToastOptions,
): string => {
    const show = toast[variant];
    if (options === undefined) {
        return show(translate(title));
    }
    const { description, timeout, action } = options;
    const id: string = show(translate(title), {
        ...(description === undefined
            ? {}
            : { description: translate(description) }),
        ...(timeout === undefined ? {} : { timeout }),
        ...(action === undefined
            ? {}
            : {
                  actionProps: {
                      children: translate(action.label),
                      size: 'sm',
                      variant: 'secondary',
                      onPress: () => {
                          toast.close(id);
                          action.onPress();
                      },
                  },
              }),
    });
    return id;
};
